// Token mirror for the Forecast Severity Map's WebGL fill.
//
// DESIGN_SYSTEM.md §2 bans raw hex outside a sanctioned per-consumer
// token-mirror file — deck.gl's GeoJsonLayer needs real RGB arrays for its
// canvas draw call, so this is that one file for MapLibreChoropleth's
// severity-tier fill and MapLegend's swatches, which must decode the same ink.
//
// Ordinal WHO/DHIS2 alert-threshold classification (§2.4 JOB B), NOT a
// continuous magnitude ramp — 4 discrete tiers, always paired with the tier
// word, never colour alone.
//
// These are a DARKENED, MONOTONIC variant of tailwind's `risk.*` hues, not a
// copy of them. The badge/chip tokens in tailwind.config.js are unchanged and
// stay the brand colours; only this map's fill diverges, for one reason:
// tailwind's risk ramp is ordinal in HUE but not in LIGHTNESS. Measured as
// composited on this map (alpha 230 over map-ground), it ran L* 60.8 normal /
// 74.2 watch / 63.0 elevated / 51.8 critical — non-monotonic, with `watch`
// the lightest thing on screen and `normal` vs `elevated` 2.2 L* apart. Under
// Vienot-Brettel-Mollon deuteranope simulation the two ENDS of the scale
// collapsed to 3.5 L* apart (normal 58.9, critical 55.4) — i.e. the reader
// who most needs the encoding cannot tell "below seasonal median" from "over
// the epidemic threshold". §1 names that failure explicitly.
//
// So the ramp is now monotonic in lightness, darkening with severity, which
// is also the direction a light-ground choropleth needs for salience:
//   composited L*  56.4 -> 47.6 -> 39.4 -> 31.2   (>= 8 L* per step)
//   deuteranope    55.7 -> 48.5 -> 42.2 -> 34.7   (>= 7.2 L* per step)
//   vs map-ground  3.22 -> 4.38 -> 5.92 -> 8.04:1 (§6.0 floor is 3:1)
// normal-vs-critical under deuteranopia goes 3.5 -> 21.0 L*. Hue family is
// preserved per tier; at these lightnesses sRGB simply has no vivid yellow,
// so `watch` reads amber-brown — that is a gamut fact, not a palette slip.
// Regenerate/verify with severityTierTheme.test.mjs, which fails the build
// if any of the three properties above regresses.
//
// `normal` is also deliberately OFF the ramp's chromatic footing: LAB chroma 17
// against 48/55/54 for the three warm tiers, at the same composited L* and the
// same hue (163 vs the 162 it had at C 46). A design review read the ramp as
// four equally-insistent colours, which is the wrong story for a WHO threshold
// ladder — "at or below the seasonal median" is the resting state, and only the
// warm tiers should carry alarm. Desaturating rather than lightening is what
// keeps that legible: every property this file guards is unchanged by it, and
// deuteranope separation from `watch` actually improves 5.7 -> 7.2 L*. The
// low-chroma property is itself pinned by a test; it is the design, not a tint.
export const SEVERITY_TIER_HEX = Object.freeze({
  normal:   '008000', // No action / Very Low — Standard Green rgb(0, 128, 0)
  watch:    'ffff00', // Be aware / Low — Pure Bright Yellow rgb(255, 255, 0)
  elevated: 'ffa500', // Be prepared / Medium — Pure Vibrant Orange rgb(255, 165, 0)
  critical: 'ff0000', // Take action / High — Pure Bright Red rgb(255, 0, 0)
})

// Text-safe stop per tier (AA on white)
export const SEVERITY_TIER_TEXT_HEX = Object.freeze({
  normal:   '008000',
  watch:    'a16207',
  elevated: 'c2410c',
  critical: 'dc2626',
})

// Outside the lightness range of every tier above — a sector with no
// forecast for the selected month is never a ramp/tier colour (§2.4).
export const NO_DATA_HEX = 'd1d5db' // gray-300

// No-data hatch stroke (§2.4/§6.2's "accessibility texture channel"). Mirrored
// here as an RGB array + 0-255 alpha because MapLibreChoropleth's deck.gl
// overlay needs real numeric colour for its GeoJsonLayer draw call; two DOM
// legends carry the same value as a CSS string. A test pins all three.
//
// gray-800 opaque, and both halves of that are forced. It was gray-500 @ 55%,
// which composited to rgb(212,216,222) on the no-data fill - 1.40:1, under
// 1.4.11's 3:1 floor for a non-text distinction that DESIGN_SYSTEM calls "the
// single most damaging error this product can make". But gray-500 OPAQUE only
// moves the problem: L* 47.9 lands 0.3 L* from `watch` (47.6), so the texture
// stops being texture and starts reading as a fifth tier.
//
// The tier ramp occupies L* 31.2-56.4, so the >= 8 L* clearance the tests
// require leaves two windows: above 64.4 or below 23.2. Above 64.4 cannot
// reach 3:1 against an L* 86.2 fill - that band is empty by construction - so
// darker is the only direction. gray-800 composites to L* 16.3: 10.26:1
// against the fill, 14.9 L* clear of the nearest tier. Duty is 17.7%, not the
// 12.5% a 3-of-24 reading suggests: the three strokes sit at x+y = 0/24/48, so
// the PERPENDICULAR pitch is 24/sqrt(2) = 16.97px, not 24. A rendered gate
// measured the hatched sector at L* 69.8-71.8 — 13.4 L* above `normal`, the
// lightest tier, so the sector still reads light and unfilled with a legible
// texture on it, which is the whole intent. That margin is also why the
// stroke stays 3px: at 4px it lands ~64.8, which is 8.4 L* over the ramp and
// sitting on the >= 8 threshold this file's own tests enforce.
export const NO_DATA_HATCH_STROKE_RGB = [31, 41, 55] // gray-800
export const NO_DATA_HATCH_STROKE_ALPHA = 255

// Water-body ink (D6 fix, design review 2026-09-02 — "water compositing
// demotes apparent severity ~2 tiers" — superseded 2026-09-03 by a product
// request to stop reading as a bordered diagonal hatch and read as a soft
// light-blue shade instead). The ORIGINAL bug: a flat translucent wash
// (sky-300 @ 115/255) drawn OVER the whole lake polygon, alpha-blending
// toward light blue everywhere it touched — that necessarily pulls every
// tier's composited colour toward the SAME point, compressing whatever
// separation the ramp had. Adjacent-tier contrast under that wash computed
// to ~1.05:1 and ~1.03:1 (see waterCompositing.test.mjs) — the water was
// reading the data, not the fill underneath it. That is still true today, so
// a flat wash stays off the table regardless of how the product ask reads.
//
// The fix keeps the no-data hatch's FillStyleExtension pattern-MASK idiom —
// a second layer, stacked on top of the choropleth, painted only on a
// minority of the tile and fully transparent everywhere else, so the
// choropleth layer's own getFillColor is never touched outside that minority
// — but swaps the geometry from diagonal hatch LINES (which is what read as
// "borders") to a dot/stipple texture (waterStippleDutyCycle() in
// hatchPattern.mjs, ~19.6% of the tile at current radius/spacing — the same
// order of magnitude as the no-data hatch's 17.7%, kept deliberately close
// so this isn't a second, uncalibrated legibility trade), plus a much lower
// ink alpha than the old hatch (140 vs 235) so even the dot pixels tint
// rather than overwrite. Sky-400, the design system's own `blue-teal` ramp
// (DESIGN_SYSTEM.md's documented water hue family, src/data/palettes.js) —
// NOT the sky-700 the old hatch and the lake/river outline shared, on
// purpose: this token is now water's alone, no code or colour shared with
// NO_DATA_HATCH_STROKE_RGB/ALPHA above.
export const WATER_TINT_INK_RGB = [30, 144, 255] // Pure vibrant water blue (#1e90ff, no purple)
export const WATER_TINT_INK_ALPHA = 225

// River line colour — vibrant blue so river channels and shorelines
// stand out with clean, crisp definition.
export const WATER_LINE_RGB = [14, 116, 220] // Rich water shoreline and river blue
export const WATER_LINE_ALPHA = 240

export const SEVERITY_TIER_ORDER = ['normal', 'watch', 'elevated', 'critical']

// `colorScale='risk'` callers speak low/medium/high/critical; the severity
// engine speaks normal/watch/elevated/critical. Both ladders mean the same
// four steps, so both resolve here and neither gets its own colour table.
//
// This lives in this file, not in the components, because it had to be true in
// two places at once: ChoroplethMap paints the polygon and MapLegend draws the
// swatch that decodes it. Two private copies is exactly how a legend ends up
// describing a map it is not painting — which this project has already shipped
// once. One table, imported twice.
export const RISK_LEVEL_TIER = Object.freeze({
  low: 'normal', medium: 'watch', high: 'elevated', critical: 'critical',
  normal: 'normal', watch: 'watch', elevated: 'elevated',
})

export function riskLevelTierHex(riskLevel) {
  const tier = RISK_LEVEL_TIER[String(riskLevel).toLowerCase()]
  return tier ? SEVERITY_TIER_HEX[tier] : null
}

// deck.gl wants [r,g,b], Leaflet wants a hex string — same table, two shapes.
// This exists so MapLibreChoropleth's fill has something importable to call:
// its private RGB conversion is what let the banned ramp survive a test that
// asserted only on riskLevelTierHex. null (not gray) on an unknown word, so
// the caller still picks its own no-data colour deliberately.
export function riskLevelTierRgb(riskLevel) {
  const hex = riskLevelTierHex(riskLevel)
  if (!hex) return null
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ]
}

export const SEVERITY_TIER_WORD = Object.freeze({
  normal: 'Normal', watch: 'Watch', elevated: 'Elevated', critical: 'Critical',
})

// WHO per-sector percentile bands exactly as the detector classifies them
// (services/model-intelligence/utils/anomalyDetector.js severity ladder):
// NORMAL <=Q50, WATCH (Q50,Q75], ELEVATED (Q75,Q90], CRITICAL >Q90 of that
// sector's own history for the same calendar month, over a 2015-2024 baseline.
// The p90/p95/p99 wording this replaces came from migration 0011 and stopped
// matching the shipped detector.
export const SEVERITY_TIER_SUBLINE = Object.freeze({
  normal:   'At or below this sector’s seasonal median (≤ Q50 of its own history for this month)',
  watch:    'Above the seasonal median, up to Q75 of this sector’s own history',
  elevated: 'Above Q75, up to Q90 — a sustained rise on this sector’s own history',
  critical: 'Above Q90 of this sector’s seasonal history — urgent response',
})

// Mirror of tailwind.config.js `theme.extend.colors.map.ground` /
// `map.glass-ink` as deck.gl TextLayer RGBA arrays. Same sanctioned-mirror
// rationale as the fill hex above: deck.gl needs real numeric colour for its
// canvas draw call, CSS custom properties don't reach it.
//
// DESIGN_SYSTEM.md §6.1/§6.4 still describe a dark viewport — that section
// predates the actual shipped ambient theme (tailwind's `map.*` block, and
// rwanda-ambient.json's own background layer, are both a LIGHT ground:
// "reads as part of IEWRS, not a black box dropped in"). These two values
// were still carrying the old dark-theme numbers (0b1220 ground, e8eef7
// near-white ink) that nothing on screen actually renders anymore — on the
// real light ground they inverted the label ink/halo pairing backwards
// (near-white ink + dark halo on a near-white map = the "no halo/contrast on
// labels" defect). Corrected to match the real tokens: map.ground (#eef3fb)
// and map.glass-ink (#1e293b, dark slate). Label ink is judged against the
// HALO, not the fill — see MAP_LABEL_HALO_RGBA below and the halo tests in
// sectorLabelPolicy.test.mjs. The old "3.48:1-7.28:1 on all 4 fills" note that
// stood here measured the wrong background and chased an unreachable target.
// Kept as the reference ground the tier fills are composited over when their
// contrast is computed; deck.gl no longer reads it directly.
export const MAP_GROUND_RGBA    = [238, 243, 251, 230] // map-ground, eef3fb

// ── The same three tokens for the react-leaflet engine (D8) ─────────────────
// Leaflet's path options take a colour and an opacity SEPARATELY (`color` +
// `opacity`, `fillColor` + `fillOpacity`) — it has no rgba-array form — so the
// tailwind `map.*` values that carry their alpha inline have to be split here
// rather than restated as literals inside the component. Hex without the `#`,
// matching NO_DATA_HEX above.
//
// These are the whole ground treatment for that engine now that its OSM
// TileLayer is gone. Multiply the alphas out and you get the tailwind values
// back exactly: rgba(15,23,42,0.35) and rgba(37,99,235,0.28).
export const MAP_GROUND_HEX             = 'eef3fb' // map-ground
export const MAP_SECTOR_STROKE_HEX      = '0f172a' // map-sector-stroke, slate-900
export const MAP_SECTOR_STROKE_OPACITY  = 0.35
// Named SILHOUETTE, not OUTLINE, because it is deliberately NOT the tailwind
// `map-country-outline` token: that one is the MapLibre style's blurred
// `national-outline-glow` and is decoration. This is the Leaflet engine's
// only statement of where the country ends, so it answers to 1.4.11.
export const MAP_COUNTRY_SILHOUETTE_HEX     = '2563eb' // map-country-silhouette, blue-600
export const MAP_COUNTRY_SILHOUETTE_OPACITY = 0.75
// Label halo. OPAQUE on purpose, unlike MAP_GROUND_RGBA above: the halo is the
// glyph's real background, and at 230 it let the fill bleed through, so the ink
// contrast moved with the tier underneath (11.66:1 worst). At 255 the ink is
// 13.13:1 regardless of tier, which is what makes the darkened ramp above safe
// for text — but only if the glyph really has this colour behind it, which is
// a property of HOW the halo is drawn, not of this alpha. It shipped once as an
// SDF outline, which cannot leave the glyph box at any setting, so nothing was
// behind the ink but the tier fill and the true ratios were 4.08/3.58/3.00/2.44.
// It is now the TextLayer's background plate. Keep it that way: the tests in
// sectorLabelPolicy.test.mjs assert the plate, not an outline.
export const MAP_LABEL_HALO_RGBA = [255, 255, 255, 255]
// The plate's hairline. The plate USED to be the map-ground colour, which made
// it exactly 1.000:1 against the ground — not "subtle", identical — so every
// one of the ~28 sector plates punched a ground-coloured hole through the tier
// fill it sat on and the choropleth looked perforated. White fixes the ink
// (14.63:1, up from 13.13) but barely separates the plate from the ground
// (1.11:1), so the edge is what makes it a chip rather than a hole: slate-400
// is 2.30:1 against the ground and 2.56:1 against the plate, so the boundary
// holds on whichever side it lands.
export const MAP_LABEL_PLATE_BORDER_RGBA = [148, 163, 184, 255] // slate-400
export const MAP_LABEL_PLATE_BORDER_PX = 1
// 3px on a plate this size is a square with the corners filed off — the shape
// reads as a bordered box, which is the dated part of the old label. 7 with the
// roomier padding makes it a chip. Geometry only: it moves no colour, so the
// ink/plate/border ratios computed above are untouched.
export const MAP_LABEL_PLATE_RADIUS_PX = 7
export const MAP_GLASS_INK_RGBA = [30, 41, 59, 255]    // map-glass-ink, 1e293b

// Ambient severity fill alpha (0-255) — 255 renders 100% pure solid opaque fill,
// exactly matching the GIS/matplotlib reference maps without any ground bleed.
export const AMBIENT_SEVERITY_FILL_ALPHA = 255

// No-data fill alpha. Was 140, which composited `e2e8f0` to (231,237,245) —
// 1.06:1 against the map-ground — indistinguishable from the ground itself,
// so the 36 non-reporting sectors read as holes punched through the country
// rather than as a state. 230 with the gray-300 ink below puts it at
// (212,216,222), 1.28:1, still recessive but visibly a third thing, and it
// stays ~30 L* clear of `normal` so it can never be mistaken for a tier.
export const NO_DATA_FILL_ALPHA = 230

// A legend swatch has to match the POLYGON, not the hex the polygon is
// authored from. Both maps draw their fill at AMBIENT_SEVERITY_FILL_ALPHA,
// so a legend that paints the raw hex opaque sits a full ramp step darker
// than the thing it decodes — measured +7.7/+10.5/+10.8/+9.6 L* on the
// Command Board, i.e. its `normal` dot matched its `watch` polygon. Compose
// the swatch the same way the map composes the fill and that whole class of
// drift goes away. Ground differs per surface (white card vs slate-50), so
// it is a parameter rather than a constant.
export function compositeOverHex(hex, alpha255, groundRgb = [255, 255, 255]) {
  const a = alpha255 / 255
  const out = [0, 2, 4].map((i, n) => {
    const fg = parseInt(hex.slice(i, i + 2), 16)
    return Math.round(fg * a + groundRgb[n] * (1 - a))
  })
  return out.map((c) => c.toString(16).padStart(2, '0')).join('')
}

export function severityTierSwatchHex(tier, groundRgb = [255, 255, 255]) {
  const hex = SEVERITY_TIER_HEX[tier] || SEVERITY_TIER_HEX.normal
  return compositeOverHex(hex, AMBIENT_SEVERITY_FILL_ALPHA, groundRgb)
}

// The legend's two swatch colours live here, not in MapLegend.jsx, so the tests
// can CALL them instead of grepping the component's source. A gate proved why:
// `backgroundColor: 'rgb(220, 38, 38)' || SEVERITY_TIER_HEX[tier]` short-circuits
// on the truthy literal, so every tier renders one banned red while a source-text
// guard sees the identifier it wants and passes. Both assertions were satisfied
// by code that never evaluates the table. A source-text test cannot decide
// reachability, and banning colour literals means enumerating every CSS colour
// syntax forever; evaluating the function ends both problems at once.
//
// Raw tier hex, deliberately NOT severityTierSwatchHex(): that helper composites
// at the ambient engine's fixed 230/255, and this legend serves the Leaflet
// engine, whose fill opacity is a user setting. Compositing at an alpha that may
// not apply would make the swatch wrong in a way that looks deliberate.
//
// `watch` no longer needs its 3:1 mitigation ring — the ring existed because
// the old amber was 2.14:1 on white; the current value is dark enough alone.
export function severitySwatchStyle(tier) {
  return { backgroundColor: `#${SEVERITY_TIER_HEX[tier]}` }
}

export function riskSwatchColor(riskLevel) {
  return `#${riskLevelTierHex(riskLevel)}`
}

// The legend's four rows, worst-first, resolved here instead of at the render
// site. Two source-text guards over MapLegend.jsx used to stand in for this: a
// positive match on `style={severitySwatchStyle(tier)}` and one on
// `[...SEVERITY_TIER_ORDER].reverse().map(`. A gate walked through both at once
// with a single line — `var tier = 'critical'` as the first statement of the
// map callback. `var` may legally redeclare a function parameter (unlike
// `let`/`const`, which throw), so every row painted critical red while both
// patterns still matched their own text and the suite stayed green.
//
// The lesson generalises past this file: source text can witness a variable's
// NAME at a call site, never its runtime BINDING. Returning rows already
// resolved is what closes it — the callback has no tier left to rebind, the
// worst-first order becomes a value a test can read rather than a regex over
// `.reverse()`, and asserting the four styles are pairwise distinct catches any
// collapse to one colour whatever syntax produced it.
export function severityLegendRows() {
  return [...SEVERITY_TIER_ORDER].reverse().map((tier) => ({
    tier,
    word: SEVERITY_TIER_WORD[tier],
    subline: SEVERITY_TIER_SUBLINE[tier],
    style: severitySwatchStyle(tier),
  }))
}
